import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SchemasState } from "./types";
import { SchemasValue } from "../../../services/api/instances/schemasApi/types";
import { ChatCompletionsMessageObject } from "../../../services/api/instances/completionsApi/types";
import {
  getSchemaMessagesReducerBuilder,
  postSchemaMessageReducerBuilder,
  postSchemaMessageToChatCompletionReducerBuilder,
  uploadFileToSchemaDialogReducerBuilder,
} from "./reducer";

const initialState: SchemasState = {
  schemaValue: null,
  hasUnsavedChanges: false,
  messages: {},
  timer: {},
  refreshToken: 0,
  temporaryScheme: null,
  isMessageLoading: {},
  isFileLoading: false,
};

const schemasSlice = createSlice({
  name: "schemas",
  initialState,
  reducers: {
    setSchemaValue: (state, action: PayloadAction<SchemasValue>) => {
      state.schemaValue = action.payload;
    },
    setUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setRefreshToken: (state) => {
      state.refreshToken += 1;
    },
    setTemporaryScheme: (
      state,
      action: PayloadAction<{ id: string; xml: string }>,
    ) => {
      const { id, xml } = action.payload;
      if (!state.temporaryScheme) {
        state.temporaryScheme = { id, xml };
        return;
      }
      state.temporaryScheme[id] = xml;
    },
    deleteTemporaryScheme: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      if (state.temporaryScheme && state.temporaryScheme[id]) {
        delete state.temporaryScheme[id];
      }
    },
    addUserMessage: {
      reducer(
        state: SchemasState,
        action: PayloadAction<{
          id: string;
          message: ChatCompletionsMessageObject;
        }>,
      ) {
        const { id, message } = action.payload;
        if (!state.messages[id]) {
          state.messages[id] = [];
        }
        state.messages[id].push(message);
      },
      prepare(id: string, content: string) {
        return {
          payload: {
            id,
            message: {
              role: "user" as const,
              content,
            },
          },
        };
      },
    },
    clearMessages: (state, action: PayloadAction<string>) => {
      delete state.messages[action.payload];
    },
    clearSchemasState: () => {
      return initialState;
    },
    requestStarted(state, action: PayloadAction<{ objectId: string }>) {
      state.timer[action.payload.objectId] = {
        status: "loading",
        startedAt: Date.now(),
        estimatedMs: 180_000,
      };
    },
    requestFinished(state, action: PayloadAction<{ objectId: string }>) {
      const entry = state.timer[action.payload.objectId];
      if (entry) entry.status = "done";
    },
  },
  extraReducers: (builder) => {
    postSchemaMessageReducerBuilder(builder);
    getSchemaMessagesReducerBuilder(builder);
    uploadFileToSchemaDialogReducerBuilder(builder);
    postSchemaMessageToChatCompletionReducerBuilder(builder);
  },
});

export const schemasReducers = schemasSlice.reducer;
export const {
  setSchemaValue,
  setUnsavedChanges,
  setRefreshToken,
  addUserMessage,
  setTemporaryScheme,
  deleteTemporaryScheme,
  clearMessages,
  requestStarted,
  requestFinished,
  clearSchemasState,
} = schemasSlice.actions;
